import React, { useState, useEffect, useMemo, useCallback } from "react";
import EmptyListState from "../../components/EmptyListState/EmptyListState";
import FullWidthAsyncButton from "../../components/FullWidthButton/FullWidthAsyncButton";
import SupersetLogoHeader from "../../components/SupersetLogoHeader/SupersetLogoHeader";
import { Link, useNavigate } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import auth from "../../fire";
import axiosClient from "../../axiosClient";
import DeleteModal from "../../components/DeleteModal/DeleteModal";
import MessageModal from "../../components/MessageModal/MessageModal";
import DropdownMenu from "../../components/DropdownMenu/DropdownMenu";
import TextSkeleton from "../../components/SkeletonComponents/TextSkeleton";

const ProgramsPage: React.FC = () => {
  const navigate = useNavigate();
  const [user, loading] = useAuthState(auth);
  const [programs, setPrograms] = useState<any[] | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [programToDelete, setProgramToDelete] = useState<String | null>(null);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [modalState, setModalState] = useState({
    isOpen: false,
    type: "",
    title: "",
    message: "",
  });
  // Should refactor to update on screen resize
  const blurbRef = React.useRef<HTMLParagraphElement>(null);
  const { innerHeight: screenHeight } = window;

  const getPrograms = useCallback(async () => {
    try {
      const getProgramsResponse = await axiosClient.get(`/api/programs`);
      const programs = getProgramsResponse.data;

      setPrograms(programs);
      setIsInitialLoading(false);
    } catch (e: any) {
      console.error(e);
      const errorMessage =
        e?.response?.data?.error || "Something unexpected occured.";
      setModalState({
        isOpen: true,
        type: "error",
        title: "Error Getting Your Programs",
        message: `${errorMessage} Please try again or contact support.`,
      });
      setIsInitialLoading(false);
    }
  }, []);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate("/login");
      return;
    }

    getPrograms();
  }, [user, loading, navigate, getPrograms]);

  const handleNewProgramClicked = async () => {
    try {
      const createProgramResponse = await axiosClient.post(`/api/programs`, {
        title: "Untitled Program",
      });

      if (createProgramResponse.status !== 200) {
        throw new Error(createProgramResponse.data.message);
      }

      const createdProgram = createProgramResponse.data;

      navigate(`/programs/${createdProgram._id}`);
    } catch (e: any) {
      console.error(e);
      const errorMessage =
        e?.response?.data?.error || "Something unexpected occured.";
      setModalState({
        isOpen: true,
        type: "error",
        title: "Error Creating Program",
        message: `${errorMessage} Please try again or contact support.`,
      });
    }
  };

  const handleDeleteProgramClicked = (programId: String) => {
    setProgramToDelete(programId);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteProgram = async () => {
    if (programs) {
      const filteredPrograms = programs.filter(
        (program) => program._id !== programToDelete
      );
      setPrograms(filteredPrograms);
    }
    setIsDeleteModalOpen(false);
    setProgramToDelete(null);

    try {
      const deleteProgramResponse = await axiosClient.delete(
        `/api/programs/${programToDelete}`
      );

      if (deleteProgramResponse.status !== 200) {
        throw new Error(deleteProgramResponse.data.message);
      }
    } catch (e: any) {
      console.error(e);
      const errorMessage =
        e?.response?.data?.error || "Something unexpected occured.";
      setModalState({
        isOpen: true,
        type: "error",
        title: "Error Deleting Program",
        message: `${errorMessage} Please try again or contact support.`,
      });
      setIsInitialLoading(true);
      getPrograms();
    }
  };

  const LOGO_HEADER = 63;
  const BLURB = blurbRef.current?.clientHeight || 40;
  const PROGRAMS_TITLE = 44;
  const NEW_PROGRAM_BTN = 70;
  const MAIN_NAV = 63;
  const OTHER_CONTENT_HEIGHT =
    LOGO_HEADER + BLURB + PROGRAMS_TITLE + NEW_PROGRAM_BTN + MAIN_NAV;

  const amountOfCardSkeletons = useMemo(() => {
    const listHeight = screenHeight - OTHER_CONTENT_HEIGHT;
    return Math.ceil(listHeight / 42);
  }, [OTHER_CONTENT_HEIGHT, screenHeight]);

  return (
    <div className="h-full relative">
      <SupersetLogoHeader />
      <p ref={blurbRef} className="p-2 pb-0 text-xs font-regular text-gray-500">
        Create a personalized program to easily populate your workout log with
        your custom plan!
      </p>
      <h1 className="p-2 font-bold text-gray-900 text-xl tracking-tight">
        Your Programs
      </h1>
      {isInitialLoading ? (
        <>
          <div
            className="px-2 overflow-hidden"
            style={{ height: `calc(100vh - ${OTHER_CONTENT_HEIGHT}px)` }}
          >
            {Array.from({ length: amountOfCardSkeletons }, (number, index) => (
              <TextSkeleton key={index} height="42px" className="w-full mb-1" />
            ))}
          </div>
        </>
      ) : (
        <div
          className="px-2 pr-0 overflow-y-scroll"
          style={{ height: `calc(100vh - ${OTHER_CONTENT_HEIGHT}px)` }}
        >
          {programs && programs.length === 0 && (
            <EmptyListState
              title="No Programs"
              message=" Create a new Program to get started."
              fullscreenList
            />
          )}
          {programs &&
            programs.length > 0 &&
            programs.map((program) => {
              return (
                <div key={program._id} className={`relative mr-2 mb-1`}>
                  <Link
                    to={`/programs/${program._id}`}
                    type="button"
                    className="grid grid-cols-[1fr_auto] content-center p-2 pr-7 bg-white rounded-md shadow-sm border border-gray-300"
                  >
                    <div className="overflow-hidden">
                      <h3 className="text-base font-medium text-gray-900  w-full whitespace-nowrap overflow-hidden text-ellipsis">
                        {program.title || "Untitled Program"}
                      </h3>
                    </div>
                  </Link>
                  <div className="absolute top-0 right-0">
                    <DropdownMenu
                      menuItems={["Delete"]}
                      parentId={program._id}
                      onDeleteClicked={() =>
                        handleDeleteProgramClicked(program._id)
                      }
                    />
                  </div>
                </div>
              );
            })}
        </div>
      )}
      <div className="py-4 px-2 w-full bg-gray-50 absolute bottom-0">
        <FullWidthAsyncButton
          text="New Program"
          loadingText="Creating Program"
          icon="PlusIcon"
          onClick={handleNewProgramClicked}
          isSkeleton={isInitialLoading}
        />
      </div>
      <DeleteModal
        isOpen={isDeleteModalOpen}
        onModalClose={() => {
          setIsDeleteModalOpen(false);
          setProgramToDelete(null);
        }}
        onSubmit={handleDeleteProgram}
        deleteTitle={"Delete Program"}
        deleteMessage={
          "Are you sure you want to delete?\nThis Program and all its data will be permanently removed. This action cannot be undone."
        }
      />
      <MessageModal
        isOpen={modalState.isOpen}
        onModalClose={() => {
          setModalState({ ...modalState, isOpen: false });
        }}
        type={modalState.type}
        title={modalState.title}
        message={modalState.message}
      />
    </div>
  );
};

export default ProgramsPage;
